import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Wallet, ArrowUpRight, ArrowDownRight, History, CreditCard, 
  Plus, RefreshCw, TrendingUp, Shield, AlertCircle,
  ArrowLeft, Home, Search, Bell, User, Filter, Download,
  Calendar, X, ChevronLeft, ChevronRight, Smartphone, Monitor
} from 'lucide-react';
import Loading from '../../components/common/Loading';
import { getTransactionHistory } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

const TransactionsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const colors = {
    primary: '#ff660e',
    black: '#0f0e0e',
    white: '#ffffff',
    lightBg: '#f8f9fa'
  };

  // Helper function to check if transaction is successful
  const isSuccessfulTransaction = (transaction) => {
    const status = transaction.status?.toLowerCase();
    return status === 'success' || status === 'successful' || status === 'completed';
  };

  // Helper function to check if transaction is pending
  const isPendingTransaction = (transaction) => {
    const status = transaction.status?.toLowerCase();
    return status === 'pending' || status === 'processing';
  };

  // Helper function to check if transaction is failed
  const isFailedTransaction = (transaction) => {
    const status = transaction.status?.toLowerCase();
    return status === 'failed' || status === 'declined' || status === 'abandoned' || status === 'reversed';
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchTransactions();
  }, [user, navigate]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getTransactionHistory();
      
      let transactionsData = [];

      if (response && response.success) {
        if (Array.isArray(response.transactions)) {
          transactionsData = response.transactions;
        } else if (Array.isArray(response.data)) {
          transactionsData = response.data;
        }
        
        // Sort by date (newest first)
        const sortedTransactions = transactionsData.sort((a, b) => 
          new Date(b.created_at || b.date) - new Date(a.created_at || a.date)
        );
        
        setTransactions(sortedTransactions);
        setFilteredTransactions(sortedTransactions);
        calculatePagination(sortedTransactions);
      } else {
        setTransactions([]);
        setFilteredTransactions([]);
      }
    } catch (error) {
      // // console.error('Error fetching transactions:', error);
      setError('Failed to load transactions. Please try again.');
      setTransactions([]);
      setFilteredTransactions([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const calculatePagination = (transactionsList) => {
    const total = transactionsList.length;
    const pages = Math.ceil(total / itemsPerPage);
    setTotalPages(pages > 0 ? pages : 1);
    setCurrentPage(1);
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        (t.description && t.description.toLowerCase().includes(term)) ||
        (t.reference && t.reference.toLowerCase().includes(term)) ||
        (t.transaction_type && t.transaction_type.toLowerCase().includes(term)) ||
        (t.type && t.type.toLowerCase().includes(term))
      );
    }

    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(t => {
        if (selectedType === 'deposit') {
          return t.type === 'credit' && t.transaction_type === 'deposit';
        } else if (selectedType === 'withdrawal') {
          return t.type === 'debit';
        } else if (selectedType === 'credit') {
          return t.type === 'credit';
        } else if (selectedType === 'debit') {
          return t.type === 'debit';
        }
        return true;
      });
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(t => 
        t.status && t.status.toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    // Date range filter
    if (dateRange.start) {
      const startDate = new Date(dateRange.start);
      filtered = filtered.filter(t => 
        new Date(t.created_at || t.date) >= startDate
      );
    }
    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(t => 
        new Date(t.created_at || t.date) <= endDate
      );
    }

    setFilteredTransactions(filtered);
    calculatePagination(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
    setSelectedStatus('all');
    setDateRange({ start: '', end: '' });
    setFilteredTransactions(transactions);
    calculatePagination(transactions);
  };

  useEffect(() => {
    applyFilters();
  }, [searchTerm, selectedType, selectedStatus, dateRange.start, dateRange.end]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchTransactions();
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-NG', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-NG', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'text-gray-600 bg-gray-50 border-gray-200';
    
    switch (status.toLowerCase()) {
      case 'success':
      case 'successful':
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'pending':
      case 'processing':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'failed':
      case 'declined':
      case 'abandoned':
      case 'reversed':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTransactionIcon = (type, transactionType) => {
    if (type === 'credit') {
      if (transactionType === 'deposit') {
        return <ArrowDownRight className="w-5 h-5 text-green-500" />;
      }
      return <ArrowDownRight className="w-5 h-5 text-blue-500" />;
    } else if (type === 'debit') {
      return <ArrowUpRight className="w-5 h-5 text-red-500" />;
    }
    return <CreditCard className="w-5 h-5 text-gray-500" />;
  };

  const getTransactionTypeLabel = (type, transactionType) => {
    if (type === 'credit' && transactionType === 'deposit') {
      return 'Deposit';
    } else if (type === 'credit' && !transactionType) {
      return 'Credit';
    } else if (type === 'debit' && transactionType === 'withdrawal') {
      return 'Withdrawal';
    } else if (type === 'debit') {
      return 'Debit';
    }
    return transactionType || type;
  };

  const handleBack = () => {
    navigate('/wallet');
  };

  // Calculate stats based on SUCCESSFUL transactions only
  const calculateStats = () => {
    const successfulTransactions = filteredTransactions.filter(isSuccessfulTransaction);
    const pendingTransactions = filteredTransactions.filter(isPendingTransaction);
    const failedTransactions = filteredTransactions.filter(isFailedTransaction);
    
    const totalCredits = successfulTransactions
      .filter(t => t.type === 'credit')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    
    const totalDebits = successfulTransactions
      .filter(t => t.type === 'debit')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    
    const netAmount = totalCredits - totalDebits;
    
    return {
      total: filteredTransactions.length,
      totalCredits,
      totalDebits,
      netAmount,
      pendingCount: pendingTransactions.length,
      failedCount: failedTransactions.length,
      successfulCount: successfulTransactions.length
    };
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Description', 'Reference', 'Type', 'Amount', 'Status', 'Net Amount'];
    const csvData = filteredTransactions.map(t => [
      t.created_at,
      t.description,
      t.reference,
      getTransactionTypeLabel(t.type, t.transaction_type),
      t.amount,
      t.status,
      t.net_amount || '0.00'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  const stats = calculateStats();

  // ========== MOBILE VIEW ==========
  const MobileView = () => (
    <div className="lg:hidden min-h-screen bg-gray-50 pb-20">
      {/* Mobile Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            
            <h1 className="text-lg font-bold text-black">Transactions</h1>
            
            <button
              onClick={handleRefresh}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-sm text-gray-600 mb-1">Total</p>
            <p className="text-xl font-bold text-black">{stats.total}</p>
            <div className="flex gap-2 mt-1">
              <span className="text-xs text-green-600">✓{stats.successfulCount}</span>
              <span className="text-xs text-yellow-600">⏳{stats.pendingCount}</span>
              <span className="text-xs text-red-600">✗{stats.failedCount}</span>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-sm text-gray-600 mb-1">Net Amount</p>
            <p className="text-xl font-bold text-black">{formatCurrency(stats.netAmount)}</p>
            <div className="flex gap-3 mt-1">
              <span className="text-xs text-green-600">+{formatCurrency(stats.totalCredits)}</span>
              <span className="text-xs text-red-600">-{formatCurrency(stats.totalDebits)}</span>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e]"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Filter Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          {(searchTerm || selectedType !== 'all' || selectedStatus !== 'all' || dateRange.start || dateRange.end) && (
            <button
              onClick={clearFilters}
              className="text-sm text-[#ff660e]"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transaction Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e]"
              >
                <option value="all">All Types</option>
                <option value="deposit">Deposits</option>
                <option value="withdrawal">Withdrawals</option>
                <option value="credit">All Credits</option>
                <option value="debit">All Debits</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e]"
              >
                <option value="all">All Status</option>
                <option value="successful">Successful</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="reversed">Reversed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e]"
                />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e]"
                />
              </div>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Transactions List */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-black">
              Transactions ({filteredTransactions.length})
            </h2>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-1 text-sm text-[#ff660e]"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
          
          {currentTransactions.length > 0 ? (
            <div className="space-y-3">
              {currentTransactions.map((transaction, index) => (
                <div
                  key={transaction.transaction_id || transaction.id || `transaction-${index}`}
                  onClick={() => navigate(`/wallet/transactions/${transaction.transaction_id || transaction.id}`)}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-gray-100">
                      {getTransactionIcon(transaction.type, transaction.transaction_type)}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-black">
                        {transaction.description || 
                         getTransactionTypeLabel(transaction.type, transaction.transaction_type)}
                      </p>
                      <p className="text-xs text-gray-500">{formatDate(transaction.created_at || transaction.date)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold text-sm ${
                      transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.type === 'credit' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </p>
                    {transaction.status && (
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">No transactions found</p>
              <p className="text-sm text-gray-500 mb-4">
                {searchTerm || selectedType !== 'all' || selectedStatus !== 'all' 
                  ? 'Try adjusting your filters'
                  : 'Start by making your first transaction'}
              </p>
              {(searchTerm || selectedType !== 'all' || selectedStatus !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-[#ff660e] text-white rounded-lg font-medium text-sm"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}

          {/* Pagination - Mobile */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );

  // ========== DESKTOP VIEW ==========
  const DesktopView = () => (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      {/* Desktop Header */}
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            <h1 className="text-2xl font-bold text-black">Transaction History</h1>
            <p className="text-gray-600">View and manage all your transactions</p>
            <p className="text-sm text-gray-500 mt-1">
              Note: Credit/Debit amounts only include successful transactions
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-[#ff660e] text-white rounded-lg hover:bg-[#ff5500]"
            >
              <Download className="w-5 h-5" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Stats Summary - Only show successful transactions in calculations */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600">Total Transactions</span>
              <CreditCard className="w-6 h-6 text-gray-500" />
            </div>
            <p className="text-3xl font-bold text-black">{stats.total}</p>
            <div className="flex gap-3 mt-2">
              <span className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded">
                ✓ {stats.successfulCount}
              </span>
              <span className="text-xs px-2 py-1 bg-yellow-50 text-yellow-700 rounded">
                ⏳ {stats.pendingCount}
              </span>
              <span className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded">
                ✗ {stats.failedCount}
              </span>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600">Total Credits</span>
              <ArrowDownRight className="w-6 h-6 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-green-600">
              {formatCurrency(stats.totalCredits)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Successful credits only</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600">Total Debits</span>
              <ArrowUpRight className="w-6 h-6 text-red-500" />
            </div>
            <p className="text-3xl font-bold text-red-600">
              {formatCurrency(stats.totalDebits)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Successful debits only</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600">Net Amount</span>
              <Wallet className="w-6 h-6" style={{ color: colors.primary }} />
            </div>
            <p className={`text-3xl font-bold ${
              stats.netAmount >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {formatCurrency(stats.netAmount)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Credits - Debits (Successful only)</p>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-black">Filters</h3>
            <div className="flex items-center gap-3">
              {(
                searchTerm || 
                selectedType !== 'all' || 
                selectedStatus !== 'all' || 
                dateRange.start || 
                dateRange.end
              ) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e]"
              >
                <option value="all">All Types</option>
                <option value="deposit">Deposits</option>
                <option value="withdrawal">Withdrawals</option>
                <option value="credit">All Credits</option>
                <option value="debit">All Debits</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e]"
              >
                <option value="all">All Status</option>
                <option value="successful">Successful</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="reversed">Reversed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e]"
                />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e]/20 focus:border-[#ff660e]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-black">
              Transactions ({filteredTransactions.length})
            </h3>
          </div>
          
          {currentTransactions.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaction
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentTransactions.map((transaction, index) => (
                      <tr 
                        key={transaction.transaction_id || transaction.id || `transaction-${index}`}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 rounded-full bg-gray-100">
                              {getTransactionIcon(transaction.type, transaction.transaction_type)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {transaction.description || 'No description'}
                              </p>
                              {transaction.reference && (
                                <p className="text-sm text-gray-500">
                                  Ref: {transaction.reference.substring(0, 12)}...
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 text-sm rounded-full bg-blue-50 text-blue-700">
                            {getTransactionTypeLabel(transaction.type, transaction.transaction_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <p className={`font-bold ${
                            transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {transaction.type === 'credit' ? '+' : '-'}
                            {formatCurrency(transaction.amount)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {isSuccessfulTransaction(transaction) ? '✓ Counted in totals' : 
                             isPendingTransaction(transaction) ? '⏳ Pending' : 
                             isFailedTransaction(transaction) ? '✗ Failed' : 'Unknown status'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1.5 text-sm rounded-full border ${getStatusColor(transaction.status)}`}>
                            {transaction.status || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDateTime(transaction.created_at || transaction.date)}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => navigate(`/wallet/transactions/${transaction.transaction_id || transaction.id}`)}
                            className="px-4 py-2 text-sm bg-[#ff660e] text-white rounded-lg hover:bg-[#ff5500] transition"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination - Desktop */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(indexOfLastItem, filteredTransactions.length)}
                      </span>{' '}
                      of <span className="font-medium">{filteredTransactions.length}</span> results
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-1 rounded-lg ${
                                currentPage === pageNum 
                                  ? 'text-white bg-[#ff660e]' 
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No transactions found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || selectedType !== 'all' || selectedStatus !== 'all' 
                  ? 'No transactions match your filters. Try changing your search criteria.'
                  : 'You haven\'t made any transactions yet.'}
              </p>
              {(searchTerm || selectedType !== 'all' || selectedStatus !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 bg-[#ff660e] text-white rounded-lg font-semibold hover:bg-[#ff5500]"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          )}
        </div>
      </main>
      
      {/* Desktop Footer */}
      <Footer />
    </div>
  );

  return (
    <>
      {/* Render Mobile or Desktop View */}
      <MobileView />
      <DesktopView />
    </>
  );
};

export default TransactionsPage;